import React, { useContext, useEffect, useState } from 'react';

import { Helmet, HelmetTags } from 'react-helmet-async';

import config from '@/appConfig';
import { AuthContext } from '@/core/auth/AuthContext';
import { useSnackbar } from '@/SnackbarProvider';
import theme from '@/theme';

const ZENDESK_SCRIPT_SRC = `https://static.zdassets.com/ekr/snippet.js?key=${config().zendesk.widgetKey}`;

declare global {
  interface Window {
    // As of this writing, types are not available from any first or third party maintainers
    zE: any;
    zESettings: any;
  }
}

type Props = {
  show: boolean;
  onClose: () => void;
}
export default function ZendeskWidget ({ show, onClose }: Props) {
  const { currentUser } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const [isInitialized, setIsInitialized] = useState(false);

  const initializeSettings = (addedTags: HelmetTags) => {
    if (window.zE) {
      setIsInitialized(true);
    }
    if (addedTags?.scriptTags) {
      const script = addedTags.scriptTags.find(({ src }) => src === ZENDESK_SCRIPT_SRC);

      script?.addEventListener('load', () => {
        window.zE('webWidget', 'hide');
        window.zE('webWidget:on', 'close', () => {
          window.zE?.hide();
          onClose();
        });
        window.zE('webWidget', 'identify', {
          name: currentUser?.displayName,
          email: currentUser?.email,
        });
        window.zE('webWidget', 'prefill', {
          name: {
            value: currentUser?.displayName,
          },
          email: {
            value: currentUser?.email,
            readOnly: true,
          },
        });
        window.zESettings = {
          webWidget: {
            color: {
              theme: theme.palette.primary,
              button: theme.palette.blue['900'],
              resultLists: theme.palette.link,
              header: theme.palette.blue['700'],
              articleLinks: theme.palette.blue['700'],
            },
          },
        };
        setIsInitialized(true);
      });
    }
  };

  useEffect(() => {
    if (show && isInitialized) {
      if (window.zE.activate) {
        window.zE.activate();
      } else {
        enqueueSnackbar('An error occurred while initializing the Zendesk widget. Please refresh or try again later.',
          { variant: 'error', preventDuplicate: true });
        onClose();
      }
    }
  }, [show, isInitialized]);

  return (
    <Helmet onChangeClientState={(_, addedTags) => initializeSettings(addedTags)}>
      <script async defer id="ze-snippet" src={ZENDESK_SCRIPT_SRC} />
    </Helmet>
  );
};
